import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Bell, Users, Store, Calendar } from 'lucide-react';
import { LOJAS_POR_EMPRESA } from '../../utils/empresas';

// =====================================================================
// ADMIN › AVISOS — gestão dos avisos de texto do Sino (Fase 2)
// =====================================================================
// Cria/edita/remove os avisos que aparecem no Sino da Home, com AGENDAMENTO
// (janela início/fim) e PÚBLICO-ALVO por perfil/loja. O tutorial da Home NÃO
// aparece aqui — ele é um card especial em código, não um aviso de texto.
//
// Regra do público: NADA selecionado = TODOS (aviso da rede). Selecionar um ou
// mais perfis/lojas restringe o aviso a quem se encaixa nos dois recortes.
// A lógica de quem vê o quê mora no backend (avisos-visibilidade.js, testada).
// ---------------------------------------------------------------------

// ⚠️ A LISTA DE PERFIS NÃO MORA MAIS AQUI (2026-08-21). Ela vem do backend
//    (`GET /api/cadastro/opcoes-acesso`, que sai de `server/perfis.js`), a mesma
//    fonte que o `AdminUsuarios` usa para GRAVAR o perfil de alguém.
//
//    Antes eram duas listas: a de lá vinda da API e esta, escrita à mão. Divergir
//    NÃO DÁ ERRO em lugar nenhum — um aviso mirado num perfil que a lista escrita
//    não conhece (ou conhece com outra grafia) simplesmente **não alcança ninguém**,
//    e a tela mostra o aviso publicado como se estivesse tudo certo. Medido em
//    19/08/2026: a lista daqui não tinha `Operacional` (dono do módulo BSV) e
//    escrevia `Processoseletivo`/`Entrevistadesligamento`, enquanto o banco grava
//    `ProcessoSeletivo`/`EntrevistaDesligamento` em CamelCase.
//
// ⚠️ `Presidencia` vem na lista (é perfil real do portal), mas não tem Home —
//    logo não vê o sino. Mirar um aviso nele não alcança ninguém. `Admin` vê tudo
//    por bypass; está na lista para o caso de se querer mirar SÓ Admin.

// Lojas: códigos CANÔNICOS de 3 letras (descarta os apelidos longos com espaço,
// ex.: 'PCI P&M'), deduplicados e ordenados. Fonte única: utils/empresas.js.
const LOJAS = [...new Set(Object.values(LOJAS_POR_EMPRESA).flat())]
    .filter((l) => !l.includes(' '))
    .sort();

const COR = {
    amarelo: '#FDBB20', vermelho: '#e11d48', texto: '#0f172a', cinza: '#64748b',
    borda: '#e2e8f0', fundoSuave: '#f8fafc', verde: '#10b981',
};

// Estado inicial do formulário (aviso novo, vazio).
const FORM_VAZIO = {
    id: null, titulo: '', resumo: '', corpo: '', imagemUrl: '', linkUrl: '', linkTexto: '',
    perfis: [], lojas: [], dataInicio: '', dataFim: '', ativo: true,
};

// Converte um DATE do banco (Date ou string ISO) para o value do <input type=date> (YYYY-MM-DD).
function paraInputDate(valor) {
    if (!valor) return '';
    return String(valor).slice(0, 10);
}

// Ajusta os perfis GRAVADOS num aviso à grafia do catálogo do backend.
//
// ⚠️ Por que existe: o recorte do sino compara em minúsculas
//    (`avisos-visibilidade.js`), então um aviso gravado como 'Processoseletivo'
//    alcança quem tem 'ProcessoSeletivo' no banco e continua funcionando. O que
//    NÃO funciona é a TELA: os chips comparam texto exato, e o perfil gravado
//    com a grafia antiga apareceria como não-marcado — quem editasse o aviso
//    salvaria sem perceber o público duplicado ('Processoseletivo' invisível no
//    array + 'ProcessoSeletivo' recém-clicado). Canonizar na leitura resolve os
//    dois: o chip marca certo e o salvar regrava na grafia do banco.
//    Perfil que não existe no catálogo é MANTIDO como está (some da tela por não
//    ter chip, mas não é apagado às escondidas de quem não pediu isso).
function canonizarPerfis(gravados, catalogo) {
    if (!Array.isArray(gravados) || !Array.isArray(catalogo)) return gravados || [];
    return gravados.map((g) => catalogo.find((c) => c.toLowerCase() === String(g).trim().toLowerCase()) || g);
}

// Resumo textual do público-alvo de um aviso (para a tabela).
function resumoPublico(lista, rotuloTodos) {
    if (!Array.isArray(lista) || lista.length === 0) return rotuloTodos;
    if (lista.length <= 3) return lista.join(', ');
    return `${lista.slice(0, 3).join(', ')} +${lista.length - 3}`;
}

// Resumo textual da janela de agendamento.
function resumoJanela(inicio, fim) {
    const i = paraInputDate(inicio), f = paraInputDate(fim);
    const br = (d) => d.split('-').reverse().join('/');
    if (!i && !f) return 'Sempre';
    if (i && !f) return `A partir de ${br(i)}`;
    if (!i && f) return `Até ${br(f)}`;
    return `${br(i)} — ${br(f)}`;
}

// Chip clicável de seleção múltipla (perfil ou loja).
function Chip({ label, ativo, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                padding: '5px 12px', borderRadius: '999px', cursor: 'pointer', fontSize: '13px',
                fontWeight: 600, transition: 'all 0.15s',
                border: `1.5px solid ${ativo ? COR.amarelo : COR.borda}`,
                backgroundColor: ativo ? '#fffaf0' : '#fff',
                color: ativo ? '#92600a' : COR.cinza,
            }}
        >
            {label}
        </button>
    );
}

const AdminAvisos = () => {
    const [avisos, setAvisos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);   // null = formulário fechado; objeto = criando/editando
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    // Catálogo de perfis do backend (fonte única — ver o comentário no topo).
    const [perfis, setPerfis] = useState([]);

    const token = localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetch('/api/avisos/admin', { headers: { Authorization: `Bearer ${token}` } });
            if (r.ok) setAvisos(await r.json());
        } catch (e) {
            console.error('Erro ao carregar avisos:', e);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { carregar(); }, [carregar]);

    // Busca o catálogo de perfis (mesma rota do AdminUsuarios — só Admin, que é
    // exatamente quem alcança esta tela).
    useEffect(() => {
        (async () => {
            try {
                const r = await fetch('/api/cadastro/opcoes-acesso', { headers: { Authorization: `Bearer ${token}` } });
                if (r.ok) {
                    const d = await r.json();
                    setPerfis(Array.isArray(d.perfis) ? d.perfis : []);
                }
            } catch (e) {
                // Silencioso de propósito: a tela avisa a ausência onde ela importa
                // (o bloco de perfis fica com o recado, em vez de uma lista vazia
                // que se lê como "não há perfil nenhum para mirar").
                console.error('Erro ao carregar perfis:', e);
            }
        })();
    }, [token]);

    // Abre o formulário para um aviso novo ou para editar um existente.
    const abrirNovo = () => { setErro(''); setForm({ ...FORM_VAZIO }); };
    const abrirEdicao = (a) => {
        setErro('');
        setForm({
            id: a.ID, titulo: a.TITULO, resumo: a.RESUMO, corpo: a.CORPO,
            imagemUrl: a.IMAGEM_URL || '',
            linkUrl: a.LINK_URL || '', linkTexto: a.LINK_TEXTO || '',
            perfis: canonizarPerfis(a.PERFIS, perfis),
            lojas: Array.isArray(a.LOJAS) ? a.LOJAS : [],
            dataInicio: paraInputDate(a.DATA_INICIO), dataFim: paraInputDate(a.DATA_FIM),
            ativo: a.ATIVO === 1 || a.ATIVO === true,
        });
    };

    // Liga/desliga um item numa lista de seleção múltipla (perfis/lojas).
    const alternar = (campo, valor) => {
        setForm((f) => {
            const atual = f[campo];
            const novo = atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor];
            return { ...f, [campo]: novo };
        });
    };

    const salvar = async (e) => {
        e.preventDefault();
        if (!form.titulo.trim() || !form.resumo.trim() || !form.corpo.trim()) {
            setErro('Título, resumo e corpo são obrigatórios.');
            return;
        }
        setSalvando(true); setErro('');
        try {
            const metodo = form.id ? 'PUT' : 'POST';
            const url = form.id ? `/api/avisos/admin/${form.id}` : '/api/avisos/admin';
            const r = await fetch(url, { method: metodo, headers, body: JSON.stringify(form) });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j.error || 'Falha ao salvar.');
            }
            setForm(null);
            await carregar();
        } catch (err) {
            setErro(err.message);
        } finally {
            setSalvando(false);
        }
    };

    const excluir = async (a) => {
        if (!window.confirm(`Excluir o aviso "${a.TITULO}"? Esta ação não pode ser desfeita.`)) return;
        try {
            const r = await fetch(`/api/avisos/admin/${a.ID}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            if (r.ok) carregar();
        } catch (e) {
            console.error('Erro ao excluir aviso:', e);
        }
    };

    const rotuloInput = { fontSize: '13px', fontWeight: 700, color: COR.texto, marginBottom: '6px', display: 'block' };
    const inputBase = {
        width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1.5px solid ${COR.borda}`,
        fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box',
    };

    return (
        <div>
            {/* Cabeçalho da seção */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ padding: '10px', borderRadius: '12px', backgroundColor: '#fffbeb', color: COR.amarelo }}>
                        <Bell size={24} />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: COR.texto }}>Avisos do Sino</h2>
                        <p style={{ margin: '2px 0 0', fontSize: '14px', color: COR.cinza }}>
                            Publique avisos na Home, com agendamento e público-alvo por perfil/loja.
                        </p>
                    </div>
                </div>
                {!form && (
                    <button
                        onClick={abrirNovo}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 18px', borderRadius: '8px', border: 'none', backgroundColor: COR.texto, color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}
                    >
                        <Plus size={18} /> Novo aviso
                    </button>
                )}
            </div>

            {/* FORMULÁRIO (criar/editar) */}
            {form && (
                <form onSubmit={salvar} style={{ border: `1px solid ${COR.borda}`, borderRadius: '12px', padding: '24px', marginBottom: '28px', backgroundColor: COR.fundoSuave }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: COR.texto }}>
                            {form.id ? 'Editar aviso' : 'Novo aviso'}
                        </h3>
                        <button type="button" onClick={() => setForm(null)} title="Fechar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: COR.cinza, display: 'flex' }}>
                            <X size={22} />
                        </button>
                    </div>

                    {/* Título + Resumo */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div>
                            <label style={rotuloInput}>Título *</label>
                            <input style={inputBase} value={form.titulo} maxLength={120}
                                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                                placeholder="Ex.: Nova política de estornos" />
                        </div>
                        <div>
                            <label style={rotuloInput}>Resumo * <span style={{ fontWeight: 400, color: COR.cinza }}>(linha no sino)</span></label>
                            <input style={inputBase} value={form.resumo} maxLength={255}
                                onChange={(e) => setForm({ ...form, resumo: e.target.value })}
                                placeholder="Uma frase curta que aparece na lista do sino" />
                        </div>
                    </div>

                    {/* Corpo */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={rotuloInput}>Corpo *</label>
                        <textarea style={{ ...inputBase, minHeight: '120px', resize: 'vertical', lineHeight: 1.5 }}
                            value={form.corpo}
                            onChange={(e) => setForm({ ...form, corpo: e.target.value })}
                            placeholder={'O texto do aviso.\n\nLinha em branco separa parágrafos.\n- comece a linha com "- " para virar lista\n**texto entre asteriscos** fica em negrito'} />
                        <p style={{ margin: '6px 0 0', fontSize: '12px', color: COR.cinza }}>
                            Formatação: <b>linha em branco</b> = novo parágrafo · <b>“- ”</b> no início = lista · <b>**texto**</b> = negrito.
                        </p>
                    </div>

                    {/* Imagem opcional */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={rotuloInput}>Imagem <span style={{ fontWeight: 400, color: COR.cinza }}>(opcional)</span></label>
                        <input style={inputBase} value={form.imagemUrl} maxLength={500}
                            onChange={(e) => setForm({ ...form, imagemUrl: e.target.value })}
                            placeholder="/avisos/nome-da-imagem.png" />
                        <p style={{ margin: '6px 0 0', fontSize: '12px', color: COR.cinza }}>
                            Caminho de uma imagem já publicada em <b>public/avisos/</b> (ex.: <code>/avisos/troco.png</code>). Aparece no topo do aviso.
                        </p>
                    </div>

                    {/* Link opcional */}
                    <div style={{ marginBottom: '4px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                            <div>
                                <label style={rotuloInput}>Link <span style={{ fontWeight: 400, color: COR.cinza }}>(opcional)</span></label>
                                <input style={inputBase} value={form.linkUrl} maxLength={500}
                                    onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
                                    placeholder="/financeiro?tab=troco" />
                            </div>
                            <div>
                                <label style={rotuloInput}>Texto do botão</label>
                                <input style={inputBase} value={form.linkTexto} maxLength={60}
                                    onChange={(e) => setForm({ ...form, linkTexto: e.target.value })}
                                    placeholder="Ex.: Abrir Troco" />
                            </div>
                        </div>
                        <p style={{ margin: '6px 0 20px', fontSize: '12px', color: COR.cinza }}>
                            Caminho interno (começa com <b>/</b>) abre a tela <b>dentro do portal</b> (ex.: <code>/financeiro?tab=troco</code>, <code>/rh?tab=solicitacoes</code>). URL externa (<b>https://…</b>) abre em aba nova.
                        </p>
                    </div>

                    {/* Público-alvo: perfis */}
                    <div style={{ marginBottom: '18px' }}>
                        <label style={rotuloInput}><Users size={15} style={{ verticalAlign: '-2px', marginRight: '5px' }} />Perfis</label>
                        <p style={{ margin: '0 0 8px', fontSize: '12px', color: COR.cinza }}>
                            {form.perfis.length === 0 ? 'Nada marcado = todos os perfis.' : `${form.perfis.length} perfil(is) selecionado(s).`}
                        </p>
                        {perfis.length === 0 ? (
                            // Sem catálogo não dá para segmentar — e uma faixa vazia de chips
                            // se leria como "não há perfil nenhum". O recado é explícito.
                            <p style={{ margin: 0, fontSize: '13px', color: COR.vermelho }}>
                                Não foi possível carregar a lista de perfis. Recarregue a página — sem ela, o aviso só pode ir para <b>todos</b>.
                            </p>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {perfis.map((p) => (
                                    <Chip key={p} label={p} ativo={form.perfis.includes(p)} onClick={() => alternar('perfis', p)} />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Público-alvo: lojas */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={rotuloInput}><Store size={15} style={{ verticalAlign: '-2px', marginRight: '5px' }} />Lojas</label>
                        <p style={{ margin: '0 0 8px', fontSize: '12px', color: COR.cinza }}>
                            {form.lojas.length === 0 ? 'Nada marcado = todas as lojas.' : `${form.lojas.length} loja(s) selecionada(s).`}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {LOJAS.map((l) => (
                                <Chip key={l} label={l} ativo={form.lojas.includes(l)} onClick={() => alternar('lojas', l)} />
                            ))}
                        </div>
                    </div>

                    {/* Agendamento + ativo */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '16px', alignItems: 'end', marginBottom: '20px' }}>
                        <div>
                            <label style={rotuloInput}><Calendar size={15} style={{ verticalAlign: '-2px', marginRight: '5px' }} />Início <span style={{ fontWeight: 400, color: COR.cinza }}>(opcional)</span></label>
                            <input type="date" style={inputBase} value={form.dataInicio}
                                onChange={(e) => setForm({ ...form, dataInicio: e.target.value })} />
                        </div>
                        <div>
                            <label style={rotuloInput}>Fim <span style={{ fontWeight: 400, color: COR.cinza }}>(opcional)</span></label>
                            <input type="date" style={inputBase} value={form.dataFim}
                                onChange={(e) => setForm({ ...form, dataFim: e.target.value })} />
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600, color: COR.texto, cursor: 'pointer', paddingBottom: '10px' }}>
                            <input type="checkbox" checked={form.ativo}
                                onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                            Ativo
                        </label>
                    </div>

                    {erro && <p style={{ color: COR.vermelho, fontSize: '14px', margin: '0 0 14px', fontWeight: 600 }}>{erro}</p>}

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="submit" disabled={salvando}
                            style={{ padding: '11px 22px', borderRadius: '8px', border: 'none', backgroundColor: COR.amarelo, color: '#5a3a00', fontWeight: 700, fontSize: '14px', cursor: salvando ? 'default' : 'pointer', opacity: salvando ? 0.6 : 1 }}>
                            {salvando ? 'Salvando...' : (form.id ? 'Salvar alterações' : 'Publicar aviso')}
                        </button>
                        <button type="button" onClick={() => setForm(null)}
                            style={{ padding: '11px 22px', borderRadius: '8px', border: `1.5px solid ${COR.borda}`, backgroundColor: '#fff', color: COR.cinza, fontWeight: 600, fontSize: '14px', cursor: 'pointer' }}>
                            Cancelar
                        </button>
                    </div>
                </form>
            )}

            {/* LISTA / TABELA */}
            {loading ? (
                <p style={{ color: COR.cinza, fontSize: '14px' }}>Carregando avisos...</p>
            ) : avisos.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 24px', color: COR.cinza, border: `1px dashed ${COR.borda}`, borderRadius: '12px' }}>
                    <Bell size={32} style={{ opacity: 0.4 }} />
                    <p style={{ margin: '12px 0 0', fontSize: '15px' }}>Nenhum aviso cadastrado ainda.</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto', border: `1px solid ${COR.borda}`, borderRadius: '12px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                            <tr style={{ backgroundColor: COR.fundoSuave, textAlign: 'left', color: COR.cinza, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                <th style={{ padding: '12px 16px', fontWeight: 700 }}>Aviso</th>
                                <th style={{ padding: '12px 16px', fontWeight: 700 }}>Perfis</th>
                                <th style={{ padding: '12px 16px', fontWeight: 700 }}>Lojas</th>
                                <th style={{ padding: '12px 16px', fontWeight: 700 }}>Janela</th>
                                <th style={{ padding: '12px 16px', fontWeight: 700 }}>Status</th>
                                <th style={{ padding: '12px 16px', fontWeight: 700, textAlign: 'right' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {avisos.map((a) => (
                                <tr key={a.ID} style={{ borderTop: `1px solid ${COR.borda}` }}>
                                    <td style={{ padding: '14px 16px' }}>
                                        <div style={{ fontWeight: 700, color: COR.texto }}>{a.TITULO}</div>
                                        <div style={{ fontSize: '13px', color: COR.cinza, marginTop: '2px' }}>{a.RESUMO}</div>
                                    </td>
                                    <td style={{ padding: '14px 16px', color: COR.cinza }}>{resumoPublico(a.PERFIS, 'Todos')}</td>
                                    <td style={{ padding: '14px 16px', color: COR.cinza }}>{resumoPublico(a.LOJAS, 'Todas')}</td>
                                    <td style={{ padding: '14px 16px', color: COR.cinza, whiteSpace: 'nowrap' }}>{resumoJanela(a.DATA_INICIO, a.DATA_FIM)}</td>
                                    <td style={{ padding: '14px 16px' }}>
                                        <span style={{
                                            fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px',
                                            backgroundColor: a.ATIVO ? '#ecfdf5' : '#f1f5f9',
                                            color: a.ATIVO ? COR.verde : COR.cinza,
                                        }}>
                                            {a.ATIVO ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <button onClick={() => abrirEdicao(a)} title="Editar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: COR.cinza, padding: '6px' }}>
                                            <Pencil size={18} />
                                        </button>
                                        <button onClick={() => excluir(a)} title="Excluir" style={{ background: 'none', border: 'none', cursor: 'pointer', color: COR.vermelho, padding: '6px' }}>
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminAvisos;
